// Maps the JSON Pointer paths of an AI suggestion (`/strategyenter/config/items/1/config/macdFastLength`,
// `/stoplosspercent`) to the wording of the strategy form ('Enter › MACD #2 › MACD Fast Length'), so the AI tab shows
// what changed in the words the user knows. Import-free, so it is tested in plain node -- see test/strategy-change-labels.js.
// The indicator modules in src/lib/indicators/*.js contain JSX and cannot be loaded there, hence a curated table of the
// keys that matter plus a readable camelCase fallback for the rest. A path that no longer resolves in the strategy (an
// outdated run) is shown as the raw path: no change is ever hidden.

const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// RFC 6901: '/a~1b/c~0d' is ['a/b', 'c~d']; ~1 is unescaped before ~0
const parsePointer = pointer => {
	if (typeof pointer !== 'string' || pointer === '') {
		return [];
	}
	return pointer
		.split('/')
		.slice(1)
		.map(token => token.replace(/~1/g, '/').replace(/~0/g, '~'));
};

// The value at the pointer, undefined as soon as a step is missing
const getAtPointer = (object, pointer) => {
	let node = object;
	const tokens = parsePointer(pointer);
	for (let i = 0; i < tokens.length; i += 1) {
		if (node === null || typeof node !== 'object' || !has(node, tokens[i])) {
			return undefined;
		}
		node = node[tokens[i]];
	}
	return node;
};

// The wording of the strategy form (src/components/common/StrategyForm.jsx for the global keys, the RSI form in
// src/lib/indicators/Rsi.js for the indicator keys); the form's own spelling, except 'Minimim' in the minimum profit text
const KEY_LABELS = {
	stoplosspercent: 'Stoploss % (real price)',
	trailingprofit: 'Profit Security › take profit at % from max',
	trailingprofitinitiation: 'Profit Security › activated at % price move',
	minprofit: 'Minimum profit %',
	preventclosewithinfirstcandles: 'Prevent close within first candles',
	prevententeronextremepricechangevalue: 'Prevent enter when pricechange % is beyond',
	prevententeronextremepricearea: 'Prevent enter › based upon #candles',
	closeonimmediatereveral: 'Close on immediate consecutive candles reversal › #candles reverting',
	closeonimmediatereveralarea:
		'Close on immediate consecutive candles reversal › started within #candles after position',
	notradeswithincandlesafterlasttradevalue: 'Prevent enter within #candles after close previous position',
	preventcloseonpricelostperiodvalue: 'Prevent close when Price lost › only during # candles',
	length: 'Length',
	maLength: 'Moving Average Length',
	upperband: 'Upper Band',
	lowerband: 'Lower Band',
	rsiLength: 'RSI Length',
};

// Words that are written in capitals in a readable label
const ACRONYMS = ['ma', 'rsi', 'macd', 'atr', 'ema', 'sma', 'wma', 'cci', 'dmi', 'adx', 'hh', 'hl', 'll', 'lh', 'btc'];

// 'maType' -> 'MA Type', 'fooBarBaz' -> 'Foo Bar Baz', 'k' -> 'K'
const keyLabel = key => {
	const text = String(key);
	if (has(KEY_LABELS, text)) {
		return KEY_LABELS[text];
	}
	return text
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.split(/\s+/)
		.filter(Boolean)
		.map(word => {
			const lower = word.toLowerCase();
			if (word.length === 1 || ACRONYMS.includes(lower)) {
				return word.toUpperCase();
			}
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(' ');
};

const SIDES = { strategyenter: 'Enter', strategyexit: 'Exit' };

const isGroup = node => typeof node?.name === 'string' && node.name.toUpperCase() === 'GROUP';

// All indicator leaves of one side in depth-first order (a group is not a leaf)
const collectLeaves = (node, leaves = []) => {
	const items = node?.config?.items;
	if (Array.isArray(items)) {
		items.forEach(child => {
			if (isGroup(child)) {
				collectLeaves(child, leaves);
			} else if (child && typeof child === 'object') {
				leaves.push(child);
			}
		});
	}
	return leaves;
};

// The crumb of an indicator leaf: its stored name (already the display name), numbered when the name occurs twice on the side
const leafCrumb = (leaf, root) => {
	const name = typeof leaf.name === 'string' && leaf.name ? leaf.name : 'Indicator';
	const sameName = collectLeaves(root).filter(other => other.name === leaf.name);
	if (sameName.length > 1) {
		return `${name} #${sameName.indexOf(leaf) + 1}`;
	}
	return name;
};

// Walks the config / items / index pairs of one side; null when the path does not resolve in this strategy
const sideLabel = (tokens, root, side) => {
	if (!root || typeof root !== 'object') {
		return null;
	}
	const crumbs = [side];
	let node = root;
	let pos = 1;
	while (tokens[pos] === 'config' && tokens[pos + 1] === 'items' && pos + 2 < tokens.length) {
		const index = tokens[pos + 2];
		const items = node?.config?.items;
		if (!Array.isArray(items) || !/^(0|[1-9]\d*)$/.test(index) || Number(index) >= items.length) {
			return null;
		}
		const child = items[Number(index)];
		if (!child || typeof child !== 'object') {
			return null;
		}
		crumbs.push(isGroup(child) ? `Group ${Number(index) + 1}` : leafCrumb(child, root));
		node = child;
		pos += 3;
	}
	if (tokens[pos] !== 'config' || pos + 2 !== tokens.length || crumbs.length < 2) {
		return null;
	}
	crumbs.push(keyLabel(tokens[pos + 1]));
	return crumbs.join(' › ');
};

// { group: 'Enter' | 'Exit' | 'Global', label, raw }; label is the raw path when it does not resolve
const changeLabel = (pointer, strategy) => {
	const raw = pointer;
	const tokens = parsePointer(pointer);
	if (tokens.length === 0) {
		return { group: 'Global', label: String(raw), raw };
	}
	if (has(SIDES, tokens[0])) {
		const side = SIDES[tokens[0]];
		const label = sideLabel(tokens, strategy?.[tokens[0]], side);
		return { group: side, label: label === null ? raw : label, raw };
	}
	return { group: 'Global', label: tokens.map(keyLabel).join(' › '), raw };
};

// A stored value as the user reads it: numbers without float noise, booleans as on / off, nothing as a dash
const formatValue = value => {
	if (value === null || value === undefined) {
		return '—';
	}
	if (typeof value === 'number') {
		return Number.isFinite(value) ? String(parseFloat(value.toPrecision(12))) : String(value);
	}
	if (typeof value === 'boolean') {
		return value ? 'on' : 'off';
	}
	if (typeof value === 'string') {
		return value;
	}
	return JSON.stringify(value);
};

const GROUP_ORDER = ['Enter', 'Exit', 'Global'];

// The changed settings of a patch grouped Enter / Exit / Global (empty groups left out), the patch order kept inside a
// group, every change kept; from is null when the change carries none
const groupChanges = (changes, strategy) => {
	const byGroup = { Enter: [], Exit: [], Global: [] };
	(Array.isArray(changes) ? changes : []).forEach(change => {
		const { group, label, raw } = changeLabel(change?.path, strategy);
		const hasFrom = change && has(change, 'from') && change.from !== undefined;
		byGroup[group].push({
			label,
			title: raw,
			from: hasFrom ? formatValue(change.from) : null,
			to: formatValue(change?.to),
		});
	});
	return GROUP_ORDER.filter(group => byGroup[group].length > 0).map(group => ({ group, items: byGroup[group] }));
};

module.exports = {
	parsePointer,
	getAtPointer,
	KEY_LABELS,
	ACRONYMS,
	keyLabel,
	changeLabel,
	formatValue,
	groupChanges,
};
